import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import InlineBanner from '../components/InlineBanner';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import WhatsappMobilePreview from '../../Whatsapp/Editor/Components/WhatsappMobilePreview';
import { getTemplateTextWithVariable } from '../../Whatsapp/Common';
import { getWaPreview, setWaPreviewOffset } from '../../../redux/reducers/smartSendWhatsAppSlice';
import { fillPreviewValues, mediaFileData, previewTemplateData, unboundIndexes } from './templateShape';
import WaSampleRowStepper from './WaSampleRowStepper';
import useWaErrorText from './errorText';
import { WaTemplateItem } from '../../../Models/DataSources/SmartSendWhatsApp';

// SmartSend part B — "כך תיראה ההודעה". A thin wrapper over the LIVE `WhatsappMobilePreview`;
// this is a translation of an existing preview, not a new design.
//
// 🔴 THE ONE THING THE WRAPPER MUST DO: fill every `{{n}}`, including the unmapped ones, with
// its own token. `getTemplateTextWithVariable` (screens/Whatsapp/Common.tsx:349-365) substitutes
// whatever the values object returns for each placeholder it finds, so a missing key prints the
// literal string "undefined" inside the bubble — which an operator reads as a value the
// recipient is going to receive. The red "not mapped" marks live on the variable row and in the
// stepper instead.
//
// The sample values come from the SERVER, already formatted (thousands separators, dates —
// contract §6 / D2). The client never formats a number for this preview.

const SAMPLE_LIMIT = 5;
// Unique DOM id: the WhatsApp editor's own preview also uses the default
// 'conversation-text-preview', and `getElementById` would otherwise measure whichever mounted
// first. The `previewId` prop exists for exactly this (v1.4 T-23).
const PREVIEW_DOM_ID = 'smartsend-wa-conversation-preview';

// The phone's outer width. The owner's own adjustment on stage (2026-09-28): about 52% of the preview
// column on his 1920px screen, which is 324px. Fixed, not a percentage — the same share of a laptop's
// narrower column would make a phone too narrow to read. `.nexus5` is `calc(100% - 31px)` + 30px
// padding of its section (whatsappStyles.js), so the device renders 1px narrower than this box.
const PHONE_WIDTH = 324;

const useStyles = makeStyles((theme) => ({
    // ONE centred block the width of the phone: the title, the sample-row stepper and the phone share
    // an axis (owner: "the text centred above the phone").
    wrap: { width: '100%', maxWidth: PHONE_WIDTH, marginLeft: 'auto', marginRight: 'auto' },
    // The title and the loading mark share ONE row whose height never changes: the mark is always
    // rendered and only its visibility follows the request. A save re-fetches the preview and so
    // does a sample switch, and a spinner ROW inserted above the phone pushed it down and back up
    // on every one of them (owner, stage 2026-09-28: "every save or sample switch makes it jump").
    titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing(1), minHeight: 28 },
    title: { fontWeight: 600 },
    stepperRow: { display: 'flex', justifyContent: 'center' },
    // A PHONE, not the column: filling the md=5 column made the "phone" ~600px wide on a large screen,
    // and the bubble wrapped nothing like it does on a real one (owner, stage 2026-09-28).
    phone: {
        width: '100%',
        // The bubble fills the chat (owner: "the green inside to 100%"). The editor's own cap is 85%
        // (`whatsappMobileMessage.maxWidth`, whatsappStyles.js:412); at this phone width that wraps
        // the text into a narrow column. Scoped to this preview: the WhatsApp editor keeps its 85%.
        '& .sent': { maxWidth: '100%' },
    },
}));

interface Props {
    campaignId: number;
    /** The app-wide sheet — `WhatsappMobilePreview` reads `whatsappStyles.js` classes off it. */
    classes: any;
    /** Bubble DOM id — only for a second phone mounted at the same time (the summary's popup). */
    previewId?: string;
}

const WhatsAppPhonePreview: React.FC<Props> = ({ campaignId, classes: appClasses, previewId = PREVIEW_DOM_ID }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // ONE definition of "turn a §4 code into words", with the `i18n.exists` gate inside it — a
    // code this build has no text for must never reach the screen as a raw key.
    const { waErrorText, isEmptyState } = useWaErrorText();

    const templates: WaTemplateItem[] = useSelector((s: any) => s.smartSendWa.waTemplates);
    const templateId: string | null = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId);
    const placeholderCount: number = useSelector((s: any) => s.smartSendWa.waPlaceholderCount ?? 0);
    const variableMap = useSelector((s: any) => s.smartSendWa.waVariableMap);
    const preview = useSelector((s: any) => s.smartSendWa.waPreview);
    const previewStatus = useSelector((s: any) => s.smartSendWa.previewStatus);
    const savedMapping = useSelector((s: any) => s.smartSendWa.waSavedMapping);
    const templatesStatus = useSelector((s: any) => s.smartSendWa.templatesStatus);
    const legTemplate = useSelector((s: any) => s.smartSendWa.waMapping?.Template);
    const legMediaUrl = useSelector((s: any) => s.smartSendWa.waMapping?.MediaUrl ?? null);
    const previewError = useSelector((s: any) => s.smartSendWa.previewError);

    const template = useMemo(
        () => templates.find((tp) => tp.TemplateId === templateId) ?? null,
        [templates, templateId],
    );

    // 🔴 THE BODY HAS EXACTLY ONE SOURCE: `GET WhatsApp/Templates` → `BodyText`.
    // There used to be a second arm here, `legTemplate?.BodyText`, and it was a fiction: the
    // server's `WaMappingTemplate` has nine fields and `BodyText` is not one of them, so the
    // fallback resolved to `undefined ?? ''` on every real response. It therefore did NOT do the
    // job its own comment claimed (covering the in-flight window) and it silently produced an
    // EMPTY BUBBLE on the screen whose next button charges per message.
    //
    // Two honest states replace it, and they are distinguishable:
    //   · the list has not loaded yet      → a spinner;
    //   · the leg points at a template that is no longer in the approved list (deleted, or no
    //     longer StatusId=3) → the real reason, `error.WA_TEMPLATE_NOT_APPROVED`, which is
    //     exactly what `TemplateIsDeleted` / `TemplateStatusId` on the leg's summary describe
    //     and exactly what the server would answer on a send (-12).
    const bodyText: string = template?.BodyText ?? '';
    const templatesLoaded = templatesStatus === 'succeeded' || templatesStatus === 'failed';
    const templateGone = !!templateId && templatesLoaded && !template;
    const legSaysUnusable = !!legTemplate
        && (legTemplate.TemplateIsDeleted === true
            || (legTemplate.TemplateStatusId != null && legTemplate.TemplateStatusId !== 3));

    // The values come from the SAVED mapping — the server resolves a row against what is
    // persisted, not against what is on screen. Re-fetching on `waSavedMapping` (written only
    // by a successful round trip) is what keeps the phone honest: it updates one beat after the
    // autosave lands, never before.
    // 09-channel-units — `campaignId` is the WhatsApp UNIT's own id (a WACampaignID) wherever this
    // renders, so the route is always told channel 3; without it the ownership gate would answer
    // 404 against dbo.Campaigns.
    const previewChannel: number = 3;

    useEffect(() => {
        if (!campaignId || !savedMapping) return;
        dispatch(getWaPreview({ campaignId, offset: preview.offset, channel: previewChannel }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, savedMapping, preview.offset, previewChannel]);

    const values = useMemo(
        () => fillPreviewValues(placeholderCount, preview.row?.Values),
        [placeholderCount, preview.row],
    );

    const renderedText = useMemo(
        () => getTemplateTextWithVariable(bodyText, values as any),
        [bodyText, values],
    );

    const fileData = useMemo(() => {
        const fromTemplate = mediaFileData(template);
        // The leg is the source of truth for media once it exists (v1.20 R17: `leg.MediaUrl`),
        // so a template whose media was replaced after the mapping was saved still previews the
        // file that will actually be sent.
        if (legMediaUrl) return { fileLink: legMediaUrl, fileType: fromTemplate.fileType };
        return fromTemplate;
    }, [template, legMediaUrl]);

    const unbound = unboundIndexes(placeholderCount, variableMap);
    const candidateCount = Math.min(preview.row?.CandidateCount ?? 0, SAMPLE_LIMIT);

    // 🔴 v1.23e · C-19 — ON THE TWO 406 EMPTY STATES THE BANNER **REPLACES** THE BUBBLE.
    //
    // The banner was rendered and the phone was rendered under it, which is the one arrangement
    // that cannot be right: the slice has ALREADY cleared `waPreview.row` and `values` for these
    // two codes (that clearing is deliberate — a stale sample under "there is nothing to show"
    // is the C14 defect in a new hat). With no values to substitute,
    // `getTemplateTextWithVariable` leaves the template's own `{{1}}…{{n}}` standing, so the
    // bubble showed the RAW TEMPLATE — placeholders and all — directly beneath a banner saying
    // there are no recipients to preview. The mock is the visual spec here: no bubble.
    //
    // ⚠️ Scoped to the two EMPTY codes, not to every failure. On a real refusal the slice keeps
    // the previous row, so the bubble still shows substituted values — stale, and explained by
    // the warning banner above it. That is a different state and a different decision.
    // WHAT IS SHOWN WHILE A NEW ANSWER IS ON ITS WAY: THE LAST SETTLED ONE. `getWaPreview.pending`
    // flips the status to 'loading', and reading that live dropped a refusal banner — or, in an empty
    // state, brought the phone back with the template's raw {{n}} — for the length of every request,
    // then restored it: the same jump the spinner row caused, in the two states it did not cover.
    // The mark in the title row says a request is out; the content waits for its answer.
    const lastSettled = useRef<{ status: string; error: string | null }>({ status: previewStatus, error: previewError });
    useEffect(() => {
        if (previewStatus !== 'loading') lastSettled.current = { status: previewStatus, error: previewError };
    }, [previewStatus, previewError]);
    const shownStatus: string = previewStatus === 'loading' ? lastSettled.current.status : previewStatus;
    const shownError: string | null = previewStatus === 'loading' ? lastSettled.current.error : previewError;

    const emptyState = shownStatus === 'failed' && isEmptyState(shownError);
    // A request in flight, or the template list still loading with nothing to show yet.
    const busy = previewStatus === 'loading' || (!templatesLoaded && !bodyText);

    return (
        <Box className={classes.wrap}>
            <Box className={classes.titleRow}>
                <Typography variant="subtitle1" className={classes.title}>
                    {t('DataSources.send.whatsapp.mapping.preview')}
                </Typography>
                <CircularProgress size={14} style={{ visibility: busy ? 'visible' : 'hidden' }} aria-hidden={!busy} />
            </Box>

            <Box className={classes.stepperRow}>
                <WaSampleRowStepper
                    offset={preview.offset}
                    // 🔴 `??`, NOT `||`, AND NOT A DEFAULT PARAMETER. `rowOffset` is
                    // `number | null` (smartSendWhatsAppSlice.ts:57), initialised to `null`
                    // (`:129`) and reset to `null` whenever the row it described stops being valid
                    // (`:626`, the two 406 empty states). An optional prop's default fires only on
                    // `undefined`, so a bare `labelOffset={preview.rowOffset}` would pass `null`
                    // through — and `null + 1 === 1` announces "שורה 1 מתוך N" over a preview that
                    // has no row at all. That is precisely the failure this decision exists to
                    // prevent, reintroduced by the punctuation.
                    // `|| ` would be wrong for the opposite reason: it swallows the legitimate
                    // `rowOffset === 0`.
                    labelOffset={preview.rowOffset ?? preview.offset}
                    count={candidateCount}
                    unboundCount={unbound.length}
                    disabled={previewStatus === 'loading'}
                    onChange={(o) => dispatch(setWaPreviewOffset(o))}
                />
            </Box>

            {/* ✅ CLOSED — OWNER DECISION, 2026-09-14. The label follows the ROW, not the click.
                The spinner stays ABOVE the bubble and the bubble does NOT blank while a new
                offset loads; what changes is that the label no longer claims a row that is not
                there yet (`labelOffset` above).
                2026-09-28 — the spinner is still above the bubble, now in the title row
                (`busy`), where it takes no room of its own: a row of its own moved the phone on
                every request.
                ⚠️ AND THIS IS DELIBERATELY NOT WHAT THE TEST DIALOG DOES — the asymmetry is the
                decision, not an oversight, so do not "fix" it into symmetry later. Same
                principle in both ("never show a label that disagrees with the content"), two
                implementations, because the constraint differs: the dialog has a button that
                spends money and therefore needs a sharp "do not act yet" (a spinner IN PLACE OF
                the text); this tab has no action at all, so stability beats interruption. */}

{/* 🔴 A REFUSED PREVIEW MUST SAY SO. `previewStatus === 'failed'` was written on two
                paths and read by nobody, so a refusal left the PREVIOUS row on screen, unchanged
                and unmarked — the operator would decide what a charged send says from a row the
                server had already declined to confirm.

                v1.21's two 406 codes are told apart from a real failure and rendered as what they
                are: `NO_CANDIDATES` and `NO_ROWS_AT_OFFSET` mean the request was understood and
                there is nothing to show, so they get the neutral `info` treatment rather than a
                warning that implies something broke. The slice has already cleared the stale row
                for both. */}
            {shownStatus === 'failed' && (
                <Box style={{ marginTop: 8 }}>
                    <InlineBanner
                        severity={isEmptyState(shownError) ? 'info' : 'warning'}
                        role="status"
                        title={waErrorText(shownError)}
                    />
                </Box>
            )}

            {/* The template the leg points at is gone from the approved list. Showing an empty
                bubble here would read as "the message is blank"; this says what is actually
                wrong, in the words the server uses for the same condition. */}
            {(templateGone || legSaysUnusable) && (
                <Box style={{ marginTop: 8 }}>
                    <InlineBanner
                        severity="error"
                        role="alert"
                        title={t('DataSources.send.whatsapp.error.WA_TEMPLATE_NOT_APPROVED')}
                    />
                </Box>
            )}

            {!emptyState && (
                <Box className={classes.phone}>
                    <WhatsappMobilePreview
                        classes={appClasses}
                        templateData={previewTemplateData(renderedText)}
                        buttonType=""
                        fileData={fileData}
                        previewId={previewId}
                    />
                </Box>
            )}
        </Box>
    );
};

export default WhatsAppPhonePreview;
